import { useState } from 'react';
import type { BillingPlan } from '@salonomy/types';
import { Button, Modal, Pill } from '@salonomy/ui';
import { useChangePlan } from '../../data/billing.js';
import { money, pluralize } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/**
 * Change plan (brief 10 §4; ADR-0011 §2). A plan change takes effect at the NEXT cycle, never mid-cycle
 * — stated plainly here so it can't surprise. The prices shown are read from the plans CONFIG (the same
 * rows the biller charges from) and remain a PROPOSAL (ADR-0010 §2) until business sign-off — labelled
 * as such, never presented as final. No price math on the client: we show the plan's own figures.
 */
export function ChangePlanModal({
  open,
  onClose,
  plans,
  currentPlanCode,
}: {
  open: boolean;
  onClose: () => void;
  plans: BillingPlan[];
  currentPlanCode: string;
}) {
  const toast = useToast();
  const changePlan = useChangePlan();
  const [selected, setSelected] = useState(currentPlanCode);

  const choices = plans.filter((p) => p.active || p.code === currentPlanCode);

  function close() {
    setSelected(currentPlanCode);
    onClose();
  }

  async function save() {
    if (selected === currentPlanCode) {
      close();
      return;
    }
    try {
      await changePlan.mutateAsync({ planCode: selected });
      toast.success('Plan change scheduled — it takes effect at your next cycle.');
      close();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change your plan.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Change plan"
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={save} disabled={selected === currentPlanCode || changePlan.isPending}>
            {changePlan.isPending ? 'Scheduling…' : 'Schedule change'}
          </Button>
        </>
      }
    >
      <p style={{ margin: '0 0 12px', fontSize: 13.5, lineHeight: 1.5 }}>
        A new plan starts at your <strong>next monthly cycle</strong> — your current cycle is never
        re-priced mid-way. You can change again any time.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {choices.map((plan) => {
          const isCurrent = plan.code === currentPlanCode;
          const isActive = plan.code === selected;
          return (
            <button
              key={plan.code}
              type="button"
              className={`billing-plan${isActive ? ' billing-plan--active' : ''}`}
              onClick={() => setSelected(plan.code)}
            >
              <div className="billing-plan__head">
                <span className="billing-plan__name">{plan.displayName}</span>
                <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {isCurrent ? <Pill tone="neutral">Current</Pill> : null}
                  {isActive ? <Pill tone="cobalt">Selected</Pill> : null}
                </span>
              </div>
              <div className="billing-plan__price">
                {plan.basePricePence > 0 ? `${money(plan.basePricePence)}/mo` : null}
                {plan.basePricePence > 0 && plan.perChairPricePence > 0 ? ' + ' : ''}
                {plan.perChairPricePence > 0 ? `${money(plan.perChairPricePence)}/chair` : null}
              </div>
              <div className="billing-plan__blurb">
                {pluralize(plan.includedSmsPerChair, 'pooled SMS')} per chair ·{' '}
                {plan.trialPeriodDays}
                -day trial
              </div>
            </button>
          );
        })}
      </div>

      <div className="muted" style={{ fontSize: 11.5, marginTop: 12 }}>
        Prices are a published proposal and may change before launch — we’ll always give you advance
        notice of any price change, and it never applies mid-cycle.
      </div>
    </Modal>
  );
}
